import '../css/index.scss';
import copy from 'copy-to-clipboard';
import React, { useEffect } from 'react';
import { QlfConfirmationDialog, useConfirmationDialog } from '@qualifio/ui-commons';
import { SaveTemplateVersionBody } from '@loyalty/shared-common/template-api-client';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLeaverPrompt } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { useTranslation } from 'react-i18next';
import { CANVAS_ID, CONTAINER_ID, DEVICE_TRANSITION_DURATION, EDITOR_ID } from '../config';
import { EditorModal } from './EditorModal';
import { EditorSidebar } from './sidebars/EditorSidebar';
import { EditorToolbar } from './toolbars/EditorToolbar';
import { LanguageToolbar } from './toolbars/LanguageToolbar';
import { MasterView, MasterViewBody, MasterViewSidebar } from '../../shared/master-details-view';
import { TemplateVersionsDropdown } from './versions-management/TemplateVersionsDropdown';
import { processComponentForCopyPaste } from '../services/copy-paste/process-component-for-copy-paste';
import { traverseComponentTree } from '../services/traverse-component-tree';
import { useCurrencies } from '../../currency/CurrenciesContext';
import { useEditorService } from '../hooks';
import { useEditorStore } from '../store';
import { useProgram } from '../../loyalty/ProgramContext';

// ============================================================================
// Styles
// ============================================================================

const useStyles = makeStyles<Theme>(({ shadows, palette }) => ({
  root: {
    height: '100%',
  },
  tabsWrapper: {
    alignItems: 'center',
    background: 'white',
    display: 'flex',
  },
  editor: {
    flexGrow: 1,
    backgroundColor: palette.background.default,
    bottom: 0,
    boxShadow: shadows[8],
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 46px)',
    left: 0,
    margin: 0,
    padding: 0,
    position: 'relative',
    top: 0,
    transition: 'opacity .3s .1s',
    width: '100%',
    zIndex: 9,
  },
  editorCanvas: {
    flex: 1,
    margin: 'auto',
    padding: 0,
    transition: `width ${DEVICE_TRANSITION_DURATION}ms`,
    width: '100%',
    '& #qlf-editor-canvas': {
      height: '100% !important',
    },
  },
}));

// ============================================================================
// Component
// ============================================================================

export function Editor(): React.ReactElement {
  const { t } = useTranslation();

  const { program } = useProgram();
  const { currencies } = useCurrencies();

  // Zustand store
  const editorService = useEditorStore(state => state.editorService);
  const selectedComponent = useEditorStore(state => state.selectedComponent);
  const isDirty = useEditorStore(state => state.isDirty);
  const customCode = useEditorStore(state => state.customCode);
  const editorTheme = useEditorStore(state => state.editorTheme);
  const setEditorState = useEditorStore(state => state.setEditorState);
  const modalContent = useEditorStore(state => state.modalContent);
  const modalIsOpen = useEditorStore(state => state.modalIsOpen);
  const closeModal = useEditorStore(state => state.closeModal);
  const getTemplateJSON = useEditorStore(state => state.getTemplateJSON);

  const classes = useStyles();
  // Local state
  const { confirmationDialogState, setConfirmationDialogState, hideConfirmationDialog } =
    useConfirmationDialog();
  const showUploadErrorDialog = (message: string) =>
    setConfirmationDialogState({
      ...confirmationDialogState,
      open: true,
      color: 'error',
      title: t('editor.upload.errors.title'),
      content: message,
      confirmButtonColor: 'secondary',
      confirmButtonText: t('editor.upload.errors.button'),
      cancelButtonText: t('generic.actions.cancel'),
      onConfirm: hideConfirmationDialog,
    });

  // ============================================================================
  // Effects
  // ============================================================================

  // Initialize editor service
  useEditorService();

  // Prompt before leaving with unsaved changes
  useLeaverPrompt(isDirty);

  useEffect(() => {
    if (editorService) {
      editorService.on('asset:upload:error', showUploadErrorDialog);
    }
  }, [editorService]);

  // Copy/paste template JSON keyboard handlers
  useEffect(() => {
    function handler(event: KeyboardEvent): void {
      if (!editorService) return;

      const isAllSelected = selectedComponent?.attributes.tagName === 'body';
      const isCopy = (event.ctrlKey || event.metaKey) && event.key === 'c';
      const isPaste = (event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'V';

      if (isAllSelected && isCopy) {
        const json = getTemplateJSON();
        copy(JSON.stringify(json));
      }

      if (isAllSelected && isPaste) {
        navigator.clipboard
          .readText()
          .then(text => {
            if (!text) return;
            const json: SaveTemplateVersionBody = JSON.parse(text);
            json.schema = traverseComponentTree(json.schema, element =>
              processComponentForCopyPaste(element, {
                destinationLocales: program.languages,
                defaultCurrencyId: currencies?.[0]?.id,
              }),
            );
            setEditorState(json);
          })
          .catch(error => console.error('Could not restore template from clipboard', error));
      }
    }

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [
    selectedComponent,
    editorService,
    editorTheme,
    customCode,
    program.languages,
    currencies,
    setEditorState,
  ]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <MasterView className={classes.root}>
      <MasterViewSidebar className={classes.sidebar}>
        <EditorSidebar />
      </MasterViewSidebar>
      <MasterViewBody>
        <EditorModal
          isOpen={modalIsOpen}
          closeModal={closeModal}
          dialogProps={modalContent?.dialogProps}
        >
          {modalContent?.content}
        </EditorModal>
        <div className={classes.tabsWrapper}>
          <div style={{ flex: 1 }}></div>
          <TemplateVersionsDropdown />
          <LanguageToolbar />
        </div>
        <div className={classes.editor} id={EDITOR_ID}>
          <EditorToolbar />
          <div className={classes.editorCanvas} id={CONTAINER_ID}>
            <div id={CANVAS_ID} />
          </div>
          <QlfConfirmationDialog {...confirmationDialogState} />
        </div>
      </MasterViewBody>
    </MasterView>
  );
}
